import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Chip,
  IconButton,
  Autocomplete,
  TextField,
  Menu,
  MenuItem,
  ListItemText,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SaveIcon from '@mui/icons-material/Save';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import FlexService from '../../services/FlexService';
import { buildJsonActions, matchesShowWhen } from './workspaceUtils';

/**
 * OrderableListPanel — Data-driven drag-and-drop reorderable list.
 *
 * JSON config (from workspace definition master block, or a content block
 * inside a "layout" view):
 * {
 *   "type": "orderable-list",
 *   "loadScript": "putaway_order_list",
 *   "primaryKey": "areaId",
 *   "displayField": "areaName",
 *   "orderField": "putawayOrder",
 *   "saveScript": "save_putaway_order",
 *   "resetScript": "reset_putaway_order",
 *   "fields": ["areaId", "areaName"],
 *   "fieldLabels": { "areaId": "Area Code", "areaName": "Area Name" },
 *   "emptyState": { "title": "No items", "message": "Nothing to order." },
 *
 *   // Optional, both off by default -- neither is needed for a fixed
 *   // universe list like putaway/allocation order:
 *   "removable": true,
 *   "addOptionsScript": "move_route_search_script",
 *   "addField": "routeId",
 *   "addLabelField": "label",
 *   "addPlaceholder": "Add a route...",
 *
 *   // Optional, for a list whose rows are also edited (workflow steps and questions, FH-371):
 *   "actions": { "header": [...], "row": [...] },  // same shape as a grid's; a row action's showWhen works too
 *   "dragWhen": { "field": "editable", "in": [true] },  // rows that can't be moved; none movable = read only
 *   "readOnlyMessage": "Copy it to change it.",       // said in place of "Drag items to reorder" then
 *   "contextAsQueryParams": true,                      // context goes to the Commands as _queryParams
 *   "rowNoun": "step"
 * }
 *
 * When used as a child content block (parentContent set on the block in a
 * "layout" view), the parent passes a `context` prop derived from
 * parentParams -- merged into every load/save/reset call as top-level FLEX
 * context variables (not nested in _payload), so the target scripts can read
 * e.g. `replenRuleId` directly. `context` is undefined for standalone
 * top-level usage (unchanged from before), so existing callers are
 * unaffected.
 *
 * Header and row actions run through the workspace's own handlers (onGlobalAction / onRowAction), so a FORM,
 * EXECUTE or NAVIGATE action does what it does on a grid; a header action gets the parent row (parentRow) for its
 * prefill.
 */

// A caption for a field with no metadata label: its name in words (areaName -> Area Name), never the raw key
const humanize = (name) => String(name).replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase());

// Sortable row component
const SortableRow = ({ id, index, item, fields, fieldLabels, primaryKey, colors, fieldMetaCache, removable, onRemove, draggable = true, rowActions = [], onRowAction }) => {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const shownActions = rowActions.filter((a) => matchesShowWhen(item, a.showWhen));
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: !draggable });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      data-testid="orderable-row"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1,
        borderBottom: `1px solid ${colors.border}`,
        bgcolor: colors.background,
      }}
    >
      <Box
        {...(draggable ? attributes : {})}
        {...(draggable ? listeners : {})}
        aria-label={draggable ? `Drag ${String(item[fields[0]] ?? '')} to move it` : undefined}
        sx={{
          visibility: draggable ? 'visible' : 'hidden',
          cursor: 'grab',
          display: 'flex',
          alignItems: 'center',
          color: colors.textMuted,
          '&:hover': { color: '#22c55e' },
          '&:active': { cursor: 'grabbing' },
        }}
      >
        <DragIndicatorIcon fontSize="small" />
      </Box>

      {/* Position number */}
      <Chip
        label={index + 1}
        size="small"
        sx={{
          minWidth: 28,
          height: 24,
          fontSize: '0.75rem',
          fontWeight: 600,
          bgcolor: 'rgba(34,197,94,0.12)',
          color: '#22c55e',
          borderRadius: '4px',
        }}
      />

      {/* Field values */}
      {fields.map((fieldName) => {
        const meta = fieldMetaCache?.[fieldName];
        const label = fieldLabels?.[fieldName] || meta?.displayLabel || humanize(fieldName);
        const value = item[fieldName];
        return (
          <Box key={fieldName} sx={{ flex: 1, minWidth: 80 }}>
            <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.65rem', display: 'block' }}>
              {label}
            </Typography>
            <Typography variant="body2" sx={{ color: colors.textPrimary, fontSize: '0.85rem' }}>
              {value !== null && value !== undefined ? String(value) : '—'}
            </Typography>
          </Box>
        );
      })}

      {shownActions.length > 0 && (
        <>
          <IconButton
            size="small"
            aria-label="Actions"
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            sx={{ color: colors.textMuted, '&:hover': { color: colors.textPrimary } }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
            {shownActions.map((a) => (
              <MenuItem
                key={a.actionId}
                onClick={() => { setMenuAnchor(null); if (onRowAction) onRowAction(a, item); }}
                sx={a.color === 'error' ? { color: '#ef4444' } : undefined}
              >
                <ListItemText>{a.label}</ListItemText>
              </MenuItem>
            ))}
          </Menu>
        </>
      )}

      {removable && (
        <IconButton
          size="small"
          onClick={() => onRemove(String(item[primaryKey]))}
          sx={{ color: colors.textMuted, '&:hover': { color: '#ef4444' } }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};

const OrderableListPanel = ({ config, colors, fieldMetaCache, context, onError, onSaved, refreshToken, onRowAction, onGlobalAction, parentRow }) => {
  const {
    title,
    loadScript,
    primaryKey = 'id',
    displayField,
    orderField,
    saveScript,
    resetScript,
    fields = [],
    fieldLabels,
    emptyState,
    removable = false,
    addOptionsScript,
    addField,
    addLabelField = 'label',
    addPlaceholder,
    dragWhen,
    readOnlyMessage,
    contextAsQueryParams = false,
    rowNoun,
  } = config;
  const { globalActions: headerActions, rowActions } = buildJsonActions(config, {});
  // what the Commands get: the parent's values as top-level variables, or as _queryParams like a grid's
  const scope = () => (contextAsQueryParams ? { _queryParams: { ...(contextRef.current || {}) } } : { ...(contextRef.current || {}) });

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [addOptions, setAddOptions] = useState([]);
  const [addValue, setAddValue] = useState(null);

  // context changes every render (a fresh object from the caller) -- read the
  // latest value through a ref inside callbacks, but key the reload effect
  // off a stringified snapshot so it only re-fires when the actual content
  // changes (e.g. a different parent row selected), not on every render.
  const contextRef = useRef(context);
  contextRef.current = context;
  const contextKey = JSON.stringify(context || {});

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Load data
  const loadData = useCallback(async () => {
    if (!loadScript) return;
    setLoading(true);
    try {
      const response = await FlexService.executeScript({
        scriptName: loadScript,
        context: { ...scope(), _payload: {} },
      });
      if (!response.success) {
        setItems([]);
        return;
      }
      const ctx = response.context || {};
      let result = ctx.result || ctx.data || ctx.response;
      if (!result) { setItems([]); return; }

      // Unwrap to array
      let rows = null;
      if (Array.isArray(result)) { rows = result; }
      else {
        for (const key of ['data', 'items', 'list', 'rows', 'results']) {
          if (Array.isArray(result[key])) { rows = result[key]; break; }
        }
      }
      if (!rows) { setItems([]); return; }

      // Sort by orderField if present
      if (orderField) {
        rows.sort((a, b) => {
          const aVal = a[orderField] ?? 999999;
          const bVal = b[orderField] ?? 999999;
          return aVal - bVal;
        });
      }

      setItems(rows);
      setHasChanges(false);
    } catch (err) {
      console.error('OrderableListPanel load failed:', err);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [loadScript, orderField]);

  useEffect(() => { loadData(); }, [contextKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // The workspace bumps refreshToken when its tab is shown again (tabs stay mounted while hidden),
  // so an area added elsewhere appears; an order dragged but not saved yet is kept.
  const hasChangesRef = useRef(hasChanges);
  hasChangesRef.current = hasChanges;
  const firstRefreshRef = useRef(true);
  useEffect(() => {
    if (firstRefreshRef.current) { firstRefreshRef.current = false; return; }
    if (!hasChangesRef.current) loadData();
  }, [refreshToken]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load "add" candidate options once (and whenever the parent context
  // changes -- e.g. a different rule selected)
  const loadAddOptions = useCallback(async () => {
    if (!addOptionsScript) return;
    try {
      const response = await FlexService.executeScript({
        scriptName: addOptionsScript,
        context: { ...scope(), _payload: {} },
      });
      if (!response.success) { setAddOptions([]); return; }
      const ctx = response.context || {};
      const result = ctx.result || ctx.data || ctx.response;
      setAddOptions(Array.isArray(result) ? result : []);
    } catch (err) {
      console.error('OrderableListPanel add-options load failed:', err);
      setAddOptions([]);
    }
  }, [addOptionsScript]);

  useEffect(() => { loadAddOptions(); }, [contextKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle drag end
  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setItems((prev) => {
      const oldIndex = prev.findIndex((item) => String(item[primaryKey]) === active.id);
      const newIndex = prev.findIndex((item) => String(item[primaryKey]) === over.id);
      return arrayMove(prev, oldIndex, newIndex);
    });
    setHasChanges(true);
  }, [primaryKey]);

  // Remove a row locally -- persisted on the next Save (saveScript already
  // replaces the whole list wholesale, so a local removal is enough; no
  // separate remove script needed).
  const handleRemove = useCallback((id) => {
    setItems((prev) => prev.filter((item) => String(item[primaryKey]) !== id));
    setHasChanges(true);
  }, [primaryKey]);

  // Add a row locally from the picked option -- also persisted on next Save.
  const handleAdd = useCallback(() => {
    if (!addValue || !addField) return;
    const newItemKey = addValue[addField];
    if (items.some((item) => String(item[primaryKey]) === String(newItemKey))) {
      setAddValue(null);
      return; // already in the list
    }
    // addOptionsScript's rows may use a different id field name than this
    // list's own primaryKey (e.g. Zone's "zoneId" vs. a hop's "hopZoneId") --
    // relabel onto primaryKey so drag id / save payload / dedupe all agree.
    const newItem = addField !== primaryKey ? { ...addValue, [primaryKey]: newItemKey } : addValue;
    setItems((prev) => [...prev, newItem]);
    setHasChanges(true);
    setAddValue(null);
  }, [addValue, addField, items, primaryKey]);

  // Save order
  const handleSave = useCallback(async () => {
    if (!saveScript) return;
    setSaving(true);
    try {
      const payload = items.map((item) => ({ [primaryKey]: item[primaryKey] }));
      const res = await FlexService.executeScript({
        scriptName: saveScript,
        context: { ...scope(), _payload: payload },
      });
      // the Command ran but may have refused the save: keep the unsaved list and say why
      if (!res?.success || res.context?.success === false) {
        if (onError) onError(res?.context?.error_message || res?.error || 'The order was not saved.');
        return;
      }
      setHasChanges(false);
      if (onSaved) onSaved('Order saved.');
      // Reload to get updated order numbers from backend
      await loadData();
    } catch (err) {
      console.error('OrderableListPanel save failed:', err);
      if (onError) onError(err.message);
    } finally {
      setSaving(false);
    }
  }, [saveScript, items, primaryKey, loadData, onError, onSaved]);

  // Reset order
  const handleReset = useCallback(async () => {
    if (!resetScript) return;
    setSaving(true);
    try {
      await FlexService.executeScript({
        scriptName: resetScript,
        context: { ...scope(), _payload: {} },
      });
      setHasChanges(false);
      await loadData();
    } catch (err) {
      console.error('OrderableListPanel reset failed:', err);
      if (onError) onError(err.message);
    } finally {
      setSaving(false);
    }
  }, [resetScript, loadData, onError]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={32} sx={{ color: '#22c55e' }} />
      </Box>
    );
  }

  const canDrag = (item) => !dragWhen || matchesShowWhen(item, dragWhen);
  const readOnly = items.length > 0 && !items.some(canDrag);
  const countText = rowNoun ? `${items.length} ${rowNoun}${items.length === 1 ? '' : 's'}.` : `${items.length} items.`;

  const availableAddOptions = addOptions.filter(
    (opt) => !items.some((item) => String(item[primaryKey]) === String(opt[addField]))
  );

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1,
          borderBottom: `1px solid ${colors.border}`,
          bgcolor: colors.surface,
        }}
      >
        <Typography variant="subtitle2" sx={{ color: colors.textSecondary, flex: 1 }}>
          {title ? `${title} — ` : ''}{readOnly ? (readOnlyMessage || 'Read only.') : 'Drag items to reorder.'} {countText}
        </Typography>
        {/* a read-only list offers no New ... (a FORM); going back still works */}
        {headerActions.filter((a) => !(readOnly && a.type === 'FORM')).map((a) => (
          <Button
            key={a.actionId}
            size="small"
            variant="outlined"
            onClick={() => onGlobalAction && onGlobalAction(a, parentRow || null)}
            sx={{ borderRadius: 0, textTransform: 'none', fontSize: '0.75rem', color: colors.textPrimary, borderColor: colors.border }}
          >
            {a.label}
          </Button>
        ))}
        {hasChanges && (
          <Chip
            label="Unsaved changes"
            size="small"
            sx={{ bgcolor: 'rgba(245,158,11,0.15)', color: '#f59e0b', borderRadius: '4px', fontSize: '0.7rem' }}
          />
        )}
        {addOptionsScript && (
          <>
            <Autocomplete
              size="small"
              options={availableAddOptions}
              getOptionLabel={(opt) => opt[addLabelField] || opt[addField] || ''}
              value={addValue}
              onChange={(e, val) => setAddValue(val)}
              sx={{ minWidth: 240 }}
              renderInput={(params) => (
                <TextField {...params} placeholder={addPlaceholder || 'Add item...'} />
              )}
            />
            <Button
              size="small"
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              onClick={handleAdd}
              disabled={!addValue}
              sx={{ borderRadius: 0, textTransform: 'none', fontSize: '0.75rem', color: colors.textSecondary }}
            >
              Add
            </Button>
          </>
        )}
        {resetScript && (
          <Button
            size="small"
            startIcon={<RestartAltIcon sx={{ fontSize: 16 }} />}
            onClick={handleReset}
            disabled={saving}
            sx={{
              borderRadius: 0,
              textTransform: 'none',
              fontSize: '0.75rem',
              color: colors.textSecondary,
              '&:hover': { color: '#ef4444' },
            }}
          >
            Reset
          </Button>
        )}
        {!readOnly && <Button
          size="small"
          variant="contained"
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon sx={{ fontSize: 16 }} />}
          onClick={handleSave}
          disabled={saving || !hasChanges}
          sx={{
            borderRadius: 0,
            textTransform: 'none',
            fontSize: '0.75rem',
            bgcolor: '#22c55e',
            '&:hover': { bgcolor: '#16a34a' },
            '&.Mui-disabled': { bgcolor: colors.surfaceElevated, color: colors.textMuted },
          }}
        >
          {saving ? 'Saving...' : 'Save Order'}
        </Button>}
      </Box>

      {items.length === 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 1 }}>
          <Typography variant="body1" sx={{ color: colors.textMuted, fontWeight: 500 }}>
            {emptyState?.title || 'No items'}
          </Typography>
          <Typography variant="body2" sx={{ color: colors.textMuted }}>
            {emptyState?.message || 'Nothing to order.'}
          </Typography>
        </Box>
      ) : (
        /* Sortable list */
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={items.map((item) => String(item[primaryKey]))}
              strategy={verticalListSortingStrategy}
            >
              {items.map((item, index) => (
                <SortableRow
                  key={String(item[primaryKey])}
                  id={String(item[primaryKey])}
                  index={index}
                  item={item}
                  fields={fields}
                  fieldLabels={fieldLabels}
                  primaryKey={primaryKey}
                  colors={colors}
                  fieldMetaCache={fieldMetaCache}
                  removable={removable}
                  onRemove={handleRemove}
                  draggable={canDrag(item)}
                  rowActions={rowActions}
                  onRowAction={onRowAction}
                />
              ))}
            </SortableContext>
          </DndContext>
        </Box>
      )}
    </Box>
  );
};

export default OrderableListPanel;
